// @ts-strict-ignore
import classNames, { type Value as ClassValue } from 'classnames'
import React, { useEffect, useState, useRef } from 'react'
import uPlot from 'uplot'
import { useUser } from '../../hooks/useUser'
import { EmptyState } from '../ui/empty-state'
import { Chart } from './chart'

type GroupedChartsProps = {
    charts: Chart[]
    chartClassName?: ClassValue
    className?: ClassValue
    empty?: any
    loading: boolean
    showFirstRowTopBorder?: boolean
}

export type Chart = {
    title: string
    data: (number | null)[][]
    lowerIsBetter?: boolean
    visible?: boolean
    overview?: {
        current: number
        previous: number
    }
    formatter?: (any) => string
    empty?: any
}

const hasData = (charts: Chart[]): boolean => {
    return charts
        .map(
            (c) =>
                c.data[0].length > 0 ||
                c.data[1].length > 0 ||
                c.data[2].length > 0 ||
                c.data[3].length > 0,
        )
        .some((c) => c)
}

export const GroupedCharts = ({
    charts,
    chartClassName = 'flex-grow m-0',
    className,
    empty,
    loading = true,
    showFirstRowTopBorder = true,
}: GroupedChartsProps) => {
    const [sync, setSync] = useState(null)
    const [containerWidth, setContainerWidth] = useState<number | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const { screenshotMode } = useUser()

    useEffect(() => {
        setSync(uPlot.sync('overview'))
    }, [])

    // Track container width for responsive grid
    useEffect(() => {
        if (!containerRef.current) return

        const resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width
            if (width > 0) {
                setContainerWidth(width)
            }
        })

        resizeObserver.observe(containerRef.current)
        return () => resizeObserver.disconnect()
    }, [])

    // If there is no data, we don't want to show the charts
    if (!loading && !hasData(charts)) {
        return (
            <EmptyState
                className="my-4"
                icon={'search'}
                title={'No data found'}
                description={
                    <span className="text-sm pb-2">
                        Please choose different filters and/or date range
                    </span>
                }
            />
        )
    }

    // Don't render charts until we have a valid container width measurement
    if (containerWidth === null) {
        return <div ref={containerRef} className="w-full" />
    }

    const visibleCharts = charts.filter((c) => c.visible !== false)

    // Determine grid columns based on container width and chart count
    let cn: string = 'grid-cols-6'
    let cols: number = 6

    if (visibleCharts.length === 2) {
        if (containerWidth < 480) {
            cn = 'grid-cols-1'
            cols = 1
        } else {
            cn = 'grid-cols-2'
            cols = 2
        }
    } else if (visibleCharts.length <= 4) {
        if (containerWidth < 480) {
            cn = 'grid-cols-1'
            cols = 1
        } else if (containerWidth < 768) {
            cn = 'grid-cols-2'
            cols = 2
        } else {
            cn = 'grid-cols-4'
            cols = 4
        }
    } else if (visibleCharts.length <= 6) {
        if (containerWidth < 480) {
            cn = 'grid-cols-1'
            cols = 1
        } else if (containerWidth < 640) {
            cn = 'grid-cols-2'
            cols = 2
        } else if (containerWidth < 1024) {
            cn = 'grid-cols-3'
            cols = 3
        } else {
            cn = 'grid-cols-6'
            cols = 6
        }
    } else if (visibleCharts.length <= 9) {
        if (containerWidth < 480) {
            cn = 'grid-cols-1'
            cols = 1
        } else if (containerWidth < 640) {
            cn = 'grid-cols-2'
            cols = 2
        } else if (containerWidth < 1024) {
            cn = 'grid-cols-3'
            cols = 3
        } else if (containerWidth < 1280) {
            cn = 'grid-cols-4'
            cols = 4
        } else {
            cn = 'grid-cols-5'
            cols = 5
        }
    } else {
        // For more than 9 charts
        if (containerWidth < 480) {
            cn = 'grid-cols-1'
            cols = 1
        } else if (containerWidth < 640) {
            cn = 'grid-cols-2'
            cols = 2
        } else if (containerWidth < 1024) {
            cn = 'grid-cols-3'
            cols = 3
        } else if (containerWidth < 1280) {
            cn = 'grid-cols-4'
            cols = 4
        } else {
            cn = 'grid-cols-6'
            cols = 6
        }
    }

    return (
        <div ref={containerRef} className={classNames('grid w-full mt-2', cn, className)}>
            {visibleCharts.map((chart, index) => {
                const isLast = index === visibleCharts.length - 1
                const isFirstInRow = index % cols === 0
                const isLastInRow = (index + 1) % cols === 0 || index === visibleCharts.length - 1
                const isInLastRow =
                    index >= visibleCharts.length - (visibleCharts.length % cols || cols)
                const isInFirstRow = index < cols

                return (
                    <Chart
                        key={chart.title}
                        title={chart.title}
                        lowerIsBetter={chart.lowerIsBetter}
                        current={chart?.overview?.current}
                        previous={chart?.overview?.previous}
                        data={chart.data ?? [[], [], [], []]}
                        formatter={chart.formatter}
                        height={85}
                        className={classNames(
                            chartClassName,
                            // Add conditional borders
                            {
                                'border-t': showFirstRowTopBorder && isInFirstRow,
                                'border-b': !isInLastRow,
                                'border-r': (isLast && isLastInRow) || !isLastInRow,
                                'rounded-bl-[6px]': isFirstInRow && isInLastRow,
                                'rounded-br-[6px]': isLastInRow && isInLastRow,
                            },
                        )}
                        sync={sync}
                        screenshotMode={screenshotMode}
                        empty={empty}
                        loading={loading}
                        compare
                    />
                )
            })}
        </div>
    )
}
